import React, { useState } from 'react';
import { Alert, App, Button } from 'antd';
import { Link } from 'react-router-dom';

import { api } from '../../api/client';
import { API_RESEND_VERIFICATION_URL, isTokenValid } from '../../utils';
import { apiErrorMessage } from '../../utils/apiErrors';
import { useCurrentUser } from '../../contexts/CurrentUserContext';

const DISMISS_KEY = 'keepr.unverifiedBannerDismissed';

/**
 * The one-line reminder above the work area while the signed-in account's
 * address is unconfirmed: sharing, uploads, keys and exports wait for it
 * (docs/SCHEMA.md § Email verification). Resend is right here; dismissing
 * hides it for the rest of this browser session.
 */
export default function UnverifiedBanner() {
	const { message } = App.useApp();
	const { user } = useCurrentUser();
	const [dismissed, setDismissed] = useState(() => {
		try { return window.sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
	});
	const [sending, setSending] = useState(false);

	if (!user || user.emailVerified !== false || dismissed || !isTokenValid()) return null;

	const resend = async () => {
		setSending(true);
		try {
			await api.post(API_RESEND_VERIFICATION_URL);
			message.success(`Confirmation email sent to ${user.email}.`);
		} catch (error) {
			message.error(apiErrorMessage(error, 'Could not send the confirmation email.'));
		} finally {
			setSending(false);
		}
	};

	const dismiss = () => {
		try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* convenience only */ }
		setDismissed(true);
	};

	return (
		<Alert
			type='warning'
			showIcon
			closable
			onClose={dismiss}
			style={{ marginBottom: 16 }}
			message={<>Confirm your email address — sharing, uploads, API keys and exports wait until <strong>{user.email}</strong> is confirmed.</>}
			action={
				<span style={{ display: 'inline-flex', gap: 8 }}>
					<Button size='small' type='primary' loading={sending} onClick={resend}>Resend email</Button>
					<Link to='/account'><Button size='small'>Account</Button></Link>
				</span>
			}
		/>
	);
}
